import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { toast } from 'sonner';
import { createPublicLead } from '../lib/api';
import 'leaflet/dist/leaflet.css';

function MapUpdater({ coordinates }) {
  const map = useMap();
  
  useEffect(() => {
    if (coordinates) {
      map.setView(coordinates, 16);
    }
  }, [coordinates, map]);

  return null;
}

export default function PublicLeadForm() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [coordinates, setCoordinates] = useState(null);
  const [formData, setFormData] = useState({
    customer_name: '',
    contact_info: {
      email: '',
      phone: ''
    },
    address: {
      street: '',
      postal_code: '',
      city: '',
      country: 'Deutschland'
    },
    notes: '',
    privacy_accepted: false
  });

  useEffect(() => {
    if (!userId) {
      console.error('Keine Vertriebler-ID in der URL gefunden');
      toast.error('Ungültige Formular-URL');
      return;
    }
    console.log('Formular geladen für Vertriebler:', userId);
  }, [userId]);

  const geocodeAddress = async (address) => {
    const query = `${address.street}, ${address.postal_code} ${address.city}, ${address.country}`;
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}`
      );
      const data = await response.json();
      
      if (data && data[0]) {
        return {
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon)
        };
      }
    } catch (error) {
      console.error('Geocoding-Fehler:', error);
    }
    return null;
  };

  const updateCoordinates = async () => {
    const { address } = formData;
    if (address.street && address.postal_code && address.city) {
      const coords = await geocodeAddress(address);
      if (coords) {
        setCoordinates([coords.lat, coords.lng]);
      }
    }
  };

  useEffect(() => {
    updateCoordinates();
  }, [formData.address]);

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        return {
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: value
          }
        };
      }
      return {
        ...prev,
        [field]: value
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userId) {
      toast.error('Keine Vertriebler-ID gefunden');
      return;
    }

    if (!formData.privacy_accepted) {
      toast.error('Bitte akzeptieren Sie die Datenschutzbestimmungen');
      return;
    }

    setLoading(true);
    try {
      const leadData = {
        ...formData,
        address: coordinates ? {
          ...formData.address,
          lat: coordinates[0],
          lng: coordinates[1]
        } : formData.address
      };
      
      console.log('Sende folgende Daten:', {
        userId,
        leadData: JSON.stringify(leadData)
      });
      
      const response = await createPublicLead(userId, leadData);
      
      console.log('Lead erfolgreich erstellt:', response);
      
      navigate(`/lead-form/${userId}/thanks`);
    } catch (error) {
      console.error('Fehler beim Erstellen des Leads:', error);
      if (error.message.includes('Vertriebler nicht gefunden')) {
        toast.error('Der ausgewählte Vertriebler wurde nicht gefunden oder ist nicht mehr aktiv');
      } else {
        toast.error('Lead konnte nicht erstellt werden. Bitte versuchen Sie es später erneut.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto space-y-8">
        <div className="text-center">
          <img src="/logo.webp" alt="Logo" className="mx-auto h-24 w-auto" />
          <h1 className="mt-6 text-3xl font-bold text-gray-900">
            Interessenten-Formular
          </h1>
          <p className="mt-2 text-gray-600">
            Bitte füllen Sie das Formular aus. Wir werden uns zeitnah mit Ihnen in Verbindung setzen.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Ihre Daten</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={formData.customer_name}
                    onChange={(e) => handleInputChange('customer_name', e.target.value)}
                    placeholder="Ihr vollständiger Name"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>E-Mail</Label>
                    <Input
                      type="email"
                      value={formData.contact_info.email}
                      onChange={(e) => handleInputChange('contact_info.email', e.target.value)}
                      placeholder="ihre@email.de"
                      required
                    />
                  </div>
                  <div>
                    <Label>Telefon</Label>
                    <Input
                      value={formData.contact_info.phone}
                      onChange={(e) => handleInputChange('contact_info.phone', e.target.value)}
                      placeholder="Ihre Telefonnummer"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label>Straße und Hausnummer</Label>
                  <Input
                    value={formData.address.street}
                    onChange={(e) => handleInputChange('address.street', e.target.value)}
                    placeholder="Straße und Hausnummer"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>PLZ</Label>
                    <Input
                      value={formData.address.postal_code}
                      onChange={(e) => handleInputChange('address.postal_code', e.target.value)}
                      placeholder="PLZ"
                      required
                    />
                  </div>
                  <div>
                    <Label>Stadt</Label>
                    <Input
                      value={formData.address.city}
                      onChange={(e) => handleInputChange('address.city', e.target.value)}
                      placeholder="Stadt"
                      required
                    />
                  </div>
                </div>

                {coordinates && (
                  <div className="h-[300px] w-full rounded-lg overflow-hidden">
                    <MapContainer
                      center={coordinates}
                      zoom={16}
                      className="h-full w-full"
                    >
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      />
                      <Marker position={coordinates} />
                      <MapUpdater coordinates={coordinates} />
                    </MapContainer>
                  </div>
                )}

                <div>
                  <Label>Anmerkungen</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    placeholder="Ihre Nachricht an uns"
                    className="h-32"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="privacy"
                    checked={formData.privacy_accepted}
                    onCheckedChange={(checked) => handleInputChange('privacy_accepted', checked)}
                  />
                  <Label htmlFor="privacy" className="text-sm">
                    Ich habe die <a href="/privacy" className="text-blue-600 hover:underline" target="_blank">Datenschutzbestimmungen</a> gelesen und akzeptiere diese
                  </Label>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={loading || !formData.privacy_accepted}
              >
                {loading ? 'Wird gesendet...' : 'Absenden'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 